import React, { useContext, useMemo, useState } from 'react';
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom';

import { Box, Button, Tab, Tabs } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import FlagIcon from '@material-ui/icons/Flag';
import { TEACHERS_PERMISSION_DENIED_URL } from 'config/urls';
import { useStudent } from 'sdk';

import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import GoalsTab from 'pages/Teachers/StudentDetail/components/GoalsTab';
import StudentSubtitle from 'pages/Teachers/StudentDetail/components/StudentSubtitle';
import { ReactComponent as BackIcon } from 'pages/Teachers/StudentListV2/assets/back-icon.svg';
import StudentsPortfolio from 'pages/Teachers/StudentPortfolio';
import { useNavbar } from 'pages/Teachers/shared';

import UserContext from 'components/UserContext';

import { ReactComponent as PortfolioIcon } from './assets/portfolio.svg';
import styles from './styles.module.scss';

const StudentDetail = () => {
  const user = useContext(UserContext);
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const { studentId, sectionId } = match.params;

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [sectionLabel] = useState(() => {
    const storageKey = `section_label_id_${sectionId}`;
    const stateLabel = location.state?.sectionLabel;

    if (stateLabel) {
      sessionStorage.setItem(storageKey, stateLabel);
      return stateLabel;
    }
    return sessionStorage.getItem(storageKey) || '';
  });

  const { data: student } = useStudent({
    studentId
  });

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title="Student Goal Progress"
          subtitle={
            <StudentSubtitle
              studentName={student?.name}
              sectionLabel={sectionLabel}
            />
          }
          Icon={BackIcon}
        />
      ),
      isTransparent: true
    }),
    [student?.name, sectionLabel]
  );

  useNavbar(navConfig);

  if (user.is_in_free_trial) {
    return <Redirect to={TEACHERS_PERMISSION_DENIED_URL} />;
  }

  const handleTabChange = ({ newValue }) => {
    setActiveTabIndex(newValue);

    if (newValue === 0 && location.search) {
      history.replace({
        pathname: location.pathname,
        state: location.state
      });
    }
  };

  // TODO: Hide the Goals tab if the section is non-math.
  return (
    <div className={styles.studentDetailWrapper}>
      <div className={styles.studentDetailContainer}>
        <div className={styles.studentDetailHeader}>
          <Tabs
            value={activeTabIndex}
            onChange={(event, value) => {
              handleTabChange({ newValue: value });
            }}
            aria-label="student detail tabs"
            indicatorColor="inherit"
            textColor="inherit"
            classes={{ indicator: styles.customTabIndicator }}
          >
            <Tab
              icon={<FlagIcon />}
              label="Goals"
              id="student-tab-0"
              aria-controls="student-tabpanel-0"
              className={styles.studentTab}
            />
            <Tab
              icon={<PortfolioIcon />}
              label="Portfolio"
              id="student-tab-1"
              aria-controls="student-tabpanel-1"
              className={styles.studentTab}
            />
          </Tabs>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            className={styles.addGoalButton}
          >
            ADD GOAL
          </Button>
        </div>

        <Box
          role="tabpanel"
          hidden={activeTabIndex !== 0}
          id="student-tabpanel-0"
          aria-labelledby="student-tab-0"
          p={3}
        >
          {activeTabIndex === 0 && <GoalsTab sectionId={sectionId} />}
        </Box>

        <Box
          role="tabpanel"
          hidden={activeTabIndex !== 1}
          id="student-tabpanel-1"
          aria-labelledby="student-tab-1"
          p={0}
        >
          {activeTabIndex === 1 && (
            <StudentsPortfolio
              propStudentId={studentId}
              history={history}
              match={match}
              isEmbedded={true}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default StudentDetail;
